import { useAppDispatch, useAppSelector } from "app/hooks";
import { Link as RouterLink } from "react-router-dom";
import { selectError, selectLoginStatus, login } from "features/auth/authSlice";
import { useForm } from "hooks/useForm";
import Spinner from "features/spinner/Spinner";
import CenterContainer from "features/containers/CenterContainer";
import { Alert, Box, Button, Card, Link, TextField, Typography } from "@mui/material";
import { LoginRequest } from "api/auth/login";

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const loginError = useAppSelector(selectError);
  const isLoading = useAppSelector(selectLoginStatus) === "loading";

  // Using form hook for validation
  const {
    handleSubmit,
    handleChange,
    data: loginData,
    errors,
  } = useForm<LoginRequest>({
    validations: {
      email: {
        pattern: {
          value:
            /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i,
          message: "Ange en giltig e-postadress.",
        },
      },
      password: {
        custom: {
          isValid: (value) => value.length > 0,
          message: "Ange ett lösenord.",
        },
      },
    },
    initialValues: {
      email: "",
      password: "",
    },
    onSubmit: () => {
      dispatch(login(loginData));
    },
  });

  return (
    <CenterContainer>
      {isLoading ? (
        <Box sx={{ display: "grid", placeItems: "center", gap: 1, my: 6 }} color="white">
          <Spinner size="3rem" />
          <Typography>Loggar in...</Typography>
        </Box>
      ) : (
        <Card elevation={8} sx={{ p: 4, borderRadius: "1rem" }}>
          <Typography variant="h1" fontSize="2rem" textAlign="center" mb={2}>
            Logga in
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 1 }}>
            <TextField
              variant="filled"
              value={loginData.email}
              onChange={handleChange("email")}
              type="email"
              placeholder="name@example.com"
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
              label="E-post"
            />

            <TextField
              variant="filled"
              value={loginData.password}
              onChange={handleChange("password")}
              type="password"
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
              label="Lösenord"
            />

            {/* Show server errors if present */}
            {loginError && Object.keys(errors).length < 1 && (
              <Alert severity="error">{loginError?.message}</Alert>
            )}

            <Button type="submit" variant="contained" size="large" sx={{ float: "right" }}>
              Logga in
            </Button>

            <Link component={RouterLink} to="/signup" underline="hover" color="primary.dark">
              Ny användare? Skapa konto!
            </Link>

            <Link component={RouterLink} to="/reset" underline="hover" color="primary.dark">
              Glömt lösenord?
            </Link>
          </Box>
        </Card>
      )}
    </CenterContainer>
  );
};

export default LoginForm;
